import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Paginated } from './defineResource';
import { TenantParams } from './provider';
import { warnInDev } from './warnInDev';

/**
 * A stable query-key stand-in for the load function itself. Functions must not go into a
 * React Query key raw: JSON.stringify serialises a function array element as null, so two
 * different loads with the same params would silently share one cache entry. Generated
 * list functions are module constants, so a per-session WeakMap id is stable.
 *
 * The contract therefore RESTS on the load being a module constant. A load created inline
 * in a component body gets a fresh identity every render → a fresh id → a fresh query key
 * → unbounded refetching. That failure mode cannot be caught structurally, so every
 * first-seen identity warns in dev (once per identity per module lifetime): a legitimate
 * module-scope load warns exactly once at its first mount, while an inline load warns on
 * every render and is immediately visible.
 */
const loadIds = new WeakMap<object, number>();
let nextLoadId = 1;

function loadId(load: object): number {
  let id = loadIds.get(load);
  if (id === undefined) {
    id = nextLoadId;
    nextLoadId += 1;
    loadIds.set(load, id);
    warnInDev(
      'first sight of a reference load; if it is defined INLINE in a component body its ' +
        'identity changes every render, so the cache key changes every render and the ' +
        'options refetch unboundedly. Pass a module-scope load (e.g. a generated function).'
    );
  }
  return id;
}

/** The pagination the shared cache always passes, so every consumer sees the same set. */
export type ReferenceQuery = { page: number; limit: number };

/**
 * Matches the generated list functions' `(scope, query?) => Promise<Paginated<T>>` shape:
 * TenantParams is assignable to every scope variant, and ReferenceQuery to ListQuery, so a
 * generated function satisfies this type without a cast.
 */
export type ReferenceLoad = (params: TenantParams, query?: ReferenceQuery) => Promise<Paginated<unknown>>;

/** The cache namespace every reference list shares. A mutation marks the whole of it stale. */
export const REFERENCE_QUERY_PREFIX = 'crud-ref';

/**
 * The shared option cache behind `reference` display columns (and, later, PR C's filter
 * pickers — spec §7.5). Reference lists are config-scale, so the load is called with an
 * explicit `{ page: 1, limit: 1000 }` — deterministic across consumers, unlike the
 * server's default page size — and cached for the session: config-scale data does not
 * churn between mounts.
 */
/**
 * The cache key behind a reference list. Exported so a non-hook caller — the CSV export
 * resolving labels at click time — can read the same entry the cells are showing, rather
 * than calling this hook once per reference column and depending on their order.
 */
export function referenceOptionsKey(load: ReferenceLoad, params: TenantParams) {
  return [REFERENCE_QUERY_PREFIX, loadId(load), params.orgSlug, params.restaurantId, params.branchId];
}

export function useReferenceOptions(load: ReferenceLoad, params: TenantParams) {
  const query = useQuery(referenceOptionsKey(load, params), () => load(params, { page: 1, limit: 1000 }), {
    staleTime: Infinity,
    // App-wide the portal disables refetch-on-mount (main.tsx: queryClient defaultOptions). A
    // staleTime-Infinity entry under that default would never refresh again, so an invalidation
    // raised by a mutation would be invisible. Opting back in here means: never refetch while
    // nothing has invalidated us, always refetch once something has.
    refetchOnMount: true,
  });
  const optionsById = useMemo(
    () => new Map((query.data?.data ?? []).map((option) => [String((option as { id?: unknown })?.id), option])),
    [query.data]
  );
  return { optionsById, isLoading: query.isLoading };
}

/** Every reference list this table renders, keyed by the load function itself. */
export type ReferenceOptionsByLoad = Map<ReferenceLoad, Map<string, unknown>>;

/**
 * Resolves every distinct reference load a table displays, in ONE observer each.
 *
 * The same query keys as useReferenceOptions, so a reference column and a reference filter chip
 * for the same field still share one cache entry (spec §7.5) — they simply no longer each mount
 * an observer per rendered cell.
 */
export function useReferenceOptionsByLoad(loads: ReferenceLoad[], params: TenantParams): ReferenceOptionsByLoad {
  const results = useQueries({
    queries: loads.map((load) => ({
      queryKey: referenceOptionsKey(load, params),
      queryFn: () => load(params, { page: 1, limit: 1000 }),
      staleTime: Infinity,
      refetchOnMount: true,
    })),
  });

  return useMemo(() => {
    const byLoad: ReferenceOptionsByLoad = new Map();
    loads.forEach((load, index) => {
      byLoad.set(
        load,
        new Map((results[index].data?.data ?? []).map((option) => [String((option as { id?: unknown })?.id), option]))
      );
    });
    return byLoad;
  }, [loads, results]);
}
